/**
 * Google My Business Integration Form
 * 
 * Form for configuring Google My Business integration with location selection and automation settings
 */

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft, ExternalLink, MapPin, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { useCreateGMBIntegration } from '@/features/integrations/hooks/useIntegrations'
import { GMBIntegrationFormData, SyncFrequency } from '@/types/integrations'

const gmbFormSchema = z.object({
  name: z.string().min(1, 'Integration name is required'),
  account_id: z.string().optional(),
  locations: z.array(z.string()).min(1, 'At least one location must be selected'),
  auto_respond_to_reviews: z.boolean().default(false),
  review_response_template: z.string().optional(),
  post_approval_required: z.boolean().default(true),
  sync_business_hours: z.boolean().default(true),
  monitor_performance: z.boolean().default(true),
  sync_frequency: z.enum(['real_time', 'every_15_minutes', 'hourly', 'daily', 'weekly', 'manual']),
})

interface GMBIntegrationFormProps {
  onSuccess: () => void
  onCancel: () => void
}

// Mock GMB locations based on franchise regions
const mockGMBLocations = [
  {
    id: 'gmb_location_1',
    name: 'FixMyBrick Toronto',
    address: '123 King St, Toronto, ON',
    verified: true,
    rating: 4.8,
    reviewCount: 245,
  },
  {
    id: 'gmb_location_2',
    name: 'FixMyBrick Hamilton',
    address: '456 Main St, Hamilton, ON',
    verified: true,
    rating: 4.9,
    reviewCount: 189,
  },
  {
    id: 'gmb_location_3',
    name: 'FixMyBrick North York',
    address: '789 Yonge St, North York, ON',
    verified: false,
    rating: 4.7,
    reviewCount: 156,
  },
  {
    id: 'gmb_location_4',
    name: 'FixMyBrick London',
    address: '321 Dundas St, London, ON',
    verified: true,
    rating: 4.6,
    reviewCount: 98,
  },
]

const syncFrequencyOptions: Array<{ value: SyncFrequency; label: string }> = [
  { value: 'real_time', label: 'Real-time (recommended)' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'manual', label: 'Manual only' },
]

const defaultResponseTemplate = `Thank you for your review! We're thrilled to hear about your positive experience with our masonry services. 

At FixMyBrick, we're committed to delivering quality craftsmanship and excellent customer service. Your feedback helps us continue to improve and serve our community better.

If you have any questions or need additional services, please don't hesitate to reach out to us.

Best regards,
The FixMyBrick Team`

export function GMBIntegrationForm({ onSuccess, onCancel }: GMBIntegrationFormProps) {
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const createIntegration = useCreateGMBIntegration()

  const form = useForm<GMBIntegrationFormData>({
    resolver: zodResolver(gmbFormSchema),
    defaultValues: {
      name: 'Google My Business',
      locations: [],
      auto_respond_to_reviews: false,
      review_response_template: defaultResponseTemplate,
      post_approval_required: true,
      sync_business_hours: true,
      monitor_performance: true,
      sync_frequency: 'daily',
    },
  })

  const onSubmit = (data: GMBIntegrationFormData) => {
    createIntegration.mutate(data, {
      onSuccess: () => {
        onSuccess()
      },
    })
  }

  const connectGoogleAccount = async () => {
    setIsConnectingGoogle(true)
    
    // Simulate OAuth flow
    try {
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // In real implementation, this would:
      // 1. Open OAuth popup
      // 2. Handle OAuth callback
      // 3. Fetch user's GMB locations
      
      form.setValue('account_id', 'mock_account_123')
      console.log('Google account connected successfully')
    } catch (error) {
      console.error('Failed to connect Google account:', error)
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  const toggleLocation = (locationId: string) => {
    const currentLocations = form.watch('locations')
    const updatedLocations = currentLocations.includes(locationId)
      ? currentLocations.filter(id => id !== locationId)
      : [...currentLocations, locationId]
    
    form.setValue('locations', updatedLocations)
    setSelectedLocations(updatedLocations)
  }

  const toggleAutoRespond = (checked: boolean) => {
    form.setValue('auto_respond_to_reviews', checked)
    if (!checked) {
      form.setValue('review_response_template', '')
    } else {
      form.setValue('review_response_template', defaultResponseTemplate)
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {/* Basic Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Configuration</CardTitle>
          <CardDescription>
            Configure your Google My Business integration settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Integration Name</Label>
            <Input
              id="name"
              {...form.register('name')}
              placeholder="Google My Business"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Google Account Connection</Label>
            {form.watch('account_id') ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-green-700">Google account connected</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => form.setValue('account_id', '')}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={connectGoogleAccount}
                disabled={isConnectingGoogle}
                className="w-full"
              >
                {isConnectingGoogle ? 'Connecting...' : 'Connect Google Account'}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Location Selection */}
      {form.watch('account_id') && (
        <Card>
          <CardHeader>
            <CardTitle>Business Locations</CardTitle>
            <CardDescription>
              Select which Google My Business locations to manage through this integration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              {mockGMBLocations.map((location) => (
                <div
                  key={location.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedLocations.includes(location.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => toggleLocation(location.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedLocations.includes(location.id)}
                        onChange={() => toggleLocation(location.id)}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{location.name}</h4>
                          {location.verified ? (
                            <Badge variant="secondary" className="text-xs">
                              Verified
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              Unverified
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {location.address}
                          </span>
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            {location.rating} ({location.reviewCount} reviews)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {form.formState.errors.locations && (
              <p className="text-sm text-red-600">{form.formState.errors.locations.message}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review Management */}
      <Card>
        <CardHeader>
          <CardTitle>Review Management</CardTitle>
          <CardDescription>
            Configure automated review responses and monitoring
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="auto_respond_to_reviews"
              checked={form.watch('auto_respond_to_reviews')}
              onCheckedChange={toggleAutoRespond}
            />
            <Label htmlFor="auto_respond_to_reviews" className="text-sm">
              Automatically respond to positive reviews (4-5 stars)
            </Label>
          </div>

          {form.watch('auto_respond_to_reviews') && (
            <div className="space-y-2">
              <Label htmlFor="review_response_template">Response Template</Label>
              <Textarea
                id="review_response_template"
                {...form.register('review_response_template')}
                rows={6}
                placeholder="Enter your automated review response template..."
              />
              <p className="text-xs text-muted-foreground">
                This template will be used for automatic responses to positive reviews. 
                You can customize it to match your brand voice.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Management */}
      <Card>
        <CardHeader>
          <CardTitle>Content Management</CardTitle>
          <CardDescription>
            Configure post scheduling and business information sync
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="post_approval_required"
              checked={form.watch('post_approval_required')}
              onCheckedChange={(checked) => form.setValue('post_approval_required', !!checked)}
            />
            <Label htmlFor="post_approval_required" className="text-sm">
              Require approval before publishing posts
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="sync_business_hours"
              checked={form.watch('sync_business_hours')}
              onCheckedChange={(checked) => form.setValue('sync_business_hours', !!checked)}
            />
            <Label htmlFor="sync_business_hours" className="text-sm">
              Sync business hours and contact information
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="monitor_performance"
              checked={form.watch('monitor_performance')}
              onCheckedChange={(checked) => form.setValue('monitor_performance', !!checked)}
            />
            <Label htmlFor="monitor_performance" className="text-sm">
              Monitor performance metrics and insights
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sync_frequency">Sync Frequency</Label>
            <Select
              value={form.watch('sync_frequency')}
              onValueChange={(value) => form.setValue('sync_frequency', value as SyncFrequency)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sync frequency" />
              </SelectTrigger>
              <SelectContent>
                {syncFrequencyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Integration Features Info */}
      <Alert>
        <AlertDescription>
          <strong>What this integration will do:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
            <li>Monitor and respond to customer reviews across all locations</li>
            <li>Schedule and publish posts to promote your services</li>
            <li>Track performance metrics including views, clicks, and calls</li>
            <li>Manage business hours and contact information</li>
            <li>Sync photos and updates from your gallery system</li>
            <li>Generate insights reports for local SEO optimization</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          type="submit" 
          disabled={createIntegration.isPending || !form.watch('account_id')}
        >
          {createIntegration.isPending ? 'Creating Integration...' : 'Create Integration'}
        </Button>
      </div>
    </form>
  )
}